import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * Avatar — image with smart fallback + optional status dot.
 *
 * Render priority: src (loaded) → fallback → initials(name) → "?"
 *
 * Three quality-of-life upgrades over the v1 API:
 *
 *  1. `fallback` — pass any ReactNode to override initials. Right for icon
 *     buttons in toolbars, status pills, AI-agent avatars where the persona
 *     is a glyph, not a name.
 *  2. `colorFromName` — when there's no image and no fallback, paint a
 *     deterministic gradient backdrop derived from the name hash. Linear /
 *     Notion default. Free polish; opt-in (false by default until consumer
 *     apps confirm they want it everywhere).
 *  3. Image-load skeleton — track image-loaded state. While `src` is
 *     fetching, show a subtle pulse instead of flashing initials → image.
 *  4. `shape` — `circle` (default, profile-picture read), `rounded`
 *     (rounded-2xl, monogram-tile / brand-placeholder read), `square`
 *     (hard corners). Together with `colorFromName`, retires the
 *     `InitialAvatar` companion components both consumer apps shipped
 *     locally. See issue #81.
 *
 * Sized via variant. Status dot positions bottom-right and reads from
 * `--success` / `--warning` / `--destructive` so dark-mode + per-studio
 * retinting work.
 */
const avatarVariants = cva(
  "relative inline-flex items-center justify-center bg-muted text-muted-foreground font-medium select-none overflow-hidden",
  {
    variants: {
      size: {
        xs: "h-6 w-6 text-[10px]",
        sm: "h-8 w-8 text-xs",
        default: "h-10 w-10 text-sm",
        lg: "h-12 w-12 text-base",
        xl: "h-16 w-16 text-lg",
      },
      shape: {
        // `circle` is the historical default — keeps existing call sites
        // pixel-identical. `rounded` and `square` are new in 0.6 to retire
        // the local `InitialAvatar` components in both consumer apps where
        // a rounded-2xl monogram tile or a hard-square brand badge is the
        // right read. See issue #81.
        circle: "rounded-full",
        rounded: "rounded-2xl",
        square: "rounded-none",
      },
    },
    defaultVariants: { size: "default", shape: "circle" },
  },
);

const statusDotVariants = cva(
  "absolute bottom-0 right-0 rounded-full ring-2 ring-background",
  {
    variants: {
      status: {
        // Token-driven so each app's --success / --warning / --destructive
        // values propagate. Was emerald-500 / amber-500 literals; broke
        // per-studio retinting + dark-mode contrast.
        online: "bg-success",
        offline: "bg-muted-foreground/40",
        busy: "bg-destructive",
        away: "bg-warning",
      },
      size: {
        xs: "h-1.5 w-1.5",
        sm: "h-2 w-2",
        default: "h-2.5 w-2.5",
        lg: "h-3 w-3",
        xl: "h-4 w-4",
      },
    },
    defaultVariants: { status: "online", size: "default" },
  },
);

export interface AvatarProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "title">,
    VariantProps<typeof avatarVariants> {
  /** Image source. If omitted or fails to load, falls through to `fallback` → initials → "?". */
  src?: string;
  /**
   * Display name. Used for `alt`, the `aria-label`, and to derive initials
   * when there's no image and no fallback. Optional — pass `fallback` if
   * you have nothing meaningful here.
   */
  name?: string;
  /**
   * Custom fallback content (icon, number, glyph). Wins over initials.
   * Shown when there's no image *or* when the image errors.
   */
  fallback?: React.ReactNode;
  /**
   * Paint a deterministic gradient backdrop derived from `name`. Same name
   * always renders the same gradient, so a chat thread of names stays
   * visually consistent across reloads. No effect when `src` is set.
   * Default `false`.
   */
  colorFromName?: boolean;
  /** Optional status dot in bottom-right. */
  status?: "online" | "offline" | "busy" | "away" | null;
}

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? "")
    .join("") || "?";

/**
 * Deterministic gradient from a name. djb2-style hash → two HSL stops 40°
 * apart. Saturation + lightness are fixed so every gradient sits in the
 * same visual range — no muddy or eye-searing pairings.
 */
function nameToGradient(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  const a = ((h % 360) + 360) % 360;
  const b = (a + 40) % 360;
  return `linear-gradient(135deg, hsl(${a} 70% 55%), hsl(${b} 70% 65%))`;
}

export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  (
    {
      className,
      size,
      shape,
      src,
      name,
      fallback,
      colorFromName = false,
      status,
      style,
      ...props
    },
    ref,
  ) => {
    const [errored, setErrored] = React.useState(false);
    const [loaded, setLoaded] = React.useState(false);

    // Reset load state when src changes — otherwise the previous image's
    // "loaded" state leaks across renders and skips the skeleton flash.
    React.useEffect(() => {
      setErrored(false);
      setLoaded(false);
    }, [src]);

    const showImage = !!src && !errored;
    const showFallback = !showImage && fallback != null;
    const showInitials = !showImage && !showFallback;
    const labelText = name ?? "Avatar";

    // Gradient backdrop — only when we'd otherwise show plain initials/?.
    // Image overrides; fallback usually wants its own bg from the icon set.
    const gradientStyle =
      colorFromName && showInitials && name
        ? { backgroundImage: nameToGradient(name) }
        : undefined;

    return (
      <span
        ref={ref}
        className={cn(avatarVariants({ size, shape }), className)}
        style={{ ...gradientStyle, ...style }}
        {...props}
      >
        {showImage ? (
          <>
            {!loaded ? (
              // Skeleton overlay while loading. Uses the same pulse the
              // existing Skeleton primitive uses; inline so we don't have
              // to import for this one-shot.
              <span
                aria-hidden
                className="absolute inset-0 animate-pulse bg-muted-foreground/10"
              />
            ) : null}
            <img
              src={src}
              alt={labelText}
              className={cn(
                "h-full w-full object-cover",
                // Tiny crossfade once the image lands so the swap doesn't
                // pop.
                "transition-opacity duration-200",
                loaded ? "opacity-100" : "opacity-0",
              )}
              onLoad={() => setLoaded(true)}
              onError={() => setErrored(true)}
            />
          </>
        ) : showFallback ? (
          <span
            aria-label={labelText}
            className={cn(
              "inline-flex items-center justify-center w-full h-full",
              // Token-readable text color. Most icons inherit `currentColor`.
              "text-foreground/80",
            )}
          >
            {fallback}
          </span>
        ) : (
          <span
            aria-label={labelText}
            className={cn(
              "font-semibold",
              // When the gradient backdrop is on, switch to white text for
              // contrast (matches the gradient's mid-saturation HSL stops).
              gradientStyle ? "text-white" : "",
            )}
          >
            {initialsOf(name ?? "")}
          </span>
        )}
        {status ? (
          <span
            className={cn(statusDotVariants({ status, size }))}
            aria-label={`Status: ${status}`}
            role="status"
          />
        ) : null}
      </span>
    );
  },
);
Avatar.displayName = "Avatar";
